/**
 * ConnectorImportModal — browse a connected Google Drive or SharePoint and
 * import selected documents into a project's always-used knowledge grounding.
 */
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Folder, FileText, ChevronRight, Loader2, X, Check, UploadCloud, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ConnectorImportModal({ provider, projectId, onClose, onImported }) {
  // Google: single folder tree. SharePoint: site -> drive -> folder tree.
  const [stage, setStage] = useState(provider === "microsoft" ? "sites" : "files");
  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState(null);
  const [drives, setDrives] = useState([]);
  const [driveId, setDriveId] = useState(null);
  const [crumbs, setCrumbs] = useState([{ id: null, name: provider === "microsoft" ? "Root" : "My Drive" }]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState({});
  const [importing, setImporting] = useState(false);
  const [keepSynced, setKeepSynced] = useState(false);

  const label = provider === "google" ? "Google Drive" : "SharePoint";

  const loadFiles = useCallback(async (folderId) => {
    setLoading(true);
    try {
      if (provider === "google") {
        const { data } = await axios.get(`${API}/connectors/google/files${folderId ? `?folder_id=${folderId}` : ""}`);
        setItems(data.items || []);
      } else {
        const { data } = await axios.get(`${API}/connectors/microsoft/files?drive_id=${driveId}${folderId ? `&item_id=${folderId}` : ""}`);
        setItems(data.items || []);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not list files"); setItems([]); }
    setLoading(false);
  }, [provider, driveId]);

  useEffect(() => {
    if (provider === "microsoft" && stage === "sites") {
      setLoading(true);
      axios.get(`${API}/connectors/microsoft/sites`).then(({ data }) => setSites(data.sites || []))
        .catch((e) => toast.error(e?.response?.data?.detail || "Could not list sites")).finally(() => setLoading(false));
    } else if (provider === "google") {
      loadFiles(null);
    }
  }, [provider, stage, loadFiles]);

  const pickSite = async (s) => {
    setSiteId(s.id); setLoading(true);
    try {
      const { data } = await axios.get(`${API}/connectors/microsoft/drives?site_id=${s.id}`);
      setDrives(data.drives || []); setStage("drives");
    } catch (e) { toast.error("Could not list drives"); }
    setLoading(false);
  };
  const pickDrive = (d) => { setDriveId(d.id); setStage("files"); };

  useEffect(() => { if (provider === "microsoft" && stage === "files" && driveId) loadFiles(null); }, [provider, stage, driveId, loadFiles]);

  const openFolder = (item) => {
    setCrumbs((c) => [...c, { id: item.id, name: item.name }]);
    loadFiles(item.id);
  };
  const goCrumb = (idx) => {
    const c = crumbs.slice(0, idx + 1);
    setCrumbs(c);
    loadFiles(c[c.length - 1].id);
  };

  const toggle = (item) => setSelected((s) => ({ ...s, [item.id]: s[item.id] ? undefined : item.name }));
  const selectedIds = Object.keys(selected).filter((k) => selected[k]);

  const doImport = async () => {
    if (selectedIds.length === 0) return;
    setImporting(true);
    try {
      const url = provider === "google" ? `${API}/connectors/google/ingest` : `${API}/connectors/microsoft/ingest`;
      const payload = provider === "google"
        ? { project_id: projectId, file_ids: selectedIds }
        : { project_id: projectId, drive_id: driveId, file_ids: selectedIds };
      const { data } = await axios.post(url, payload);
      toast.success(`Imported ${data.ingested.length} file(s)${data.skipped.length ? `, skipped ${data.skipped.length}` : ""}`);
      if (keepSynced) {
        try {
          const curFolder = crumbs[crumbs.length - 1];
          await axios.post(`${API}/connectors/sync`, {
            project_id: projectId, provider,
            folder_id: curFolder?.id || null,
            drive_id: provider === "microsoft" ? driveId : null,
            folder_name: curFolder?.name || "Folder",
          });
          toast.success("Folder will auto-sync new files");
        } catch (e) { toast.error(e?.response?.data?.detail || "Could not enable sync"); }
      }
      onImported();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    setImporting(false);
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 p-4" onClick={onClose} data-testid="connector-import-modal">
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-2xl border border-[var(--qn-border)] bg-[var(--qn-card)] flex flex-col" style={{ maxHeight: "80vh" }}>
        <div className="flex items-center justify-between p-4 border-b border-[var(--qn-border)]">
          <h2 className="text-base font-bold text-[var(--qn-foreground)]">Import from {label}</h2>
          <button onClick={onClose} className="p-1.5 rounded text-[var(--qn-foreground-muted)] hover:text-[var(--qn-foreground)]"><X className="w-4 h-4" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading && <div className="flex items-center gap-2 text-[var(--qn-foreground-muted)] text-sm py-8 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>}

          {!loading && provider === "microsoft" && stage === "sites" && (
            <div className="space-y-1.5" data-testid="connector-sites">
              {sites.length === 0 && <p className="text-sm text-[var(--qn-foreground-muted)]">No sites found.</p>}
              {sites.map((s) => (
                <button key={s.id} onClick={() => pickSite(s)} className="w-full flex items-center gap-2 text-left rounded-lg border border-[var(--qn-border)] bg-[var(--qn-surface)] px-3 py-2 hover:border-[var(--qn-primary)]/50">
                  <Folder className="w-4 h-4 text-[var(--qn-primary)]" /> <span className="text-sm text-[var(--qn-foreground)] flex-1 truncate">{s.name}</span> <ChevronRight className="w-4 h-4 text-[var(--qn-foreground-muted)]" />
                </button>
              ))}
            </div>
          )}

          {!loading && provider === "microsoft" && stage === "drives" && (
            <div className="space-y-1.5" data-testid="connector-drives">
              <button onClick={() => setStage("sites")} className="flex items-center gap-1 text-xs text-[var(--qn-foreground-muted)] mb-2"><ArrowLeft className="w-3 h-3" /> Sites</button>
              {drives.map((d) => (
                <button key={d.id} onClick={() => pickDrive(d)} className="w-full flex items-center gap-2 text-left rounded-lg border border-[var(--qn-border)] bg-[var(--qn-surface)] px-3 py-2 hover:border-[var(--qn-primary)]/50">
                  <Folder className="w-4 h-4 text-[var(--qn-primary)]" /> <span className="text-sm text-[var(--qn-foreground)] flex-1 truncate">{d.name}</span> <ChevronRight className="w-4 h-4 text-[var(--qn-foreground-muted)]" />
                </button>
              ))}
            </div>
          )}

          {!loading && stage === "files" && (
            <>
              <div className="flex items-center gap-1 flex-wrap text-xs text-[var(--qn-foreground-muted)] mb-3">
                {crumbs.map((c, i) => (
                  <span key={i} className="flex items-center gap-1">
                    <button onClick={() => goCrumb(i)} className="hover:text-[var(--qn-primary)]">{c.name}</button>
                    {i < crumbs.length - 1 && <ChevronRight className="w-3 h-3" />}
                  </span>
                ))}
              </div>
              <div className="space-y-1" data-testid="connector-files">
                {items.length === 0 && <p className="text-sm text-[var(--qn-foreground-muted)]">Empty folder.</p>}
                {items.map((it) => it.is_folder ? (
                  <button key={it.id} onClick={() => openFolder(it)} data-testid={`connector-folder-${it.id}`}
                    className="w-full flex items-center gap-2 text-left rounded-lg px-3 py-2 hover:bg-[var(--qn-surface-hover)]">
                    <Folder className="w-4 h-4 text-amber-500" /> <span className="text-sm text-[var(--qn-foreground)] flex-1 truncate">{it.name}</span> <ChevronRight className="w-4 h-4 text-[var(--qn-foreground-muted)]" />
                  </button>
                ) : (
                  <label key={it.id} data-testid={`connector-file-${it.id}`} className="w-full flex items-center gap-2 rounded-lg px-3 py-2 hover:bg-[var(--qn-surface-hover)] cursor-pointer">
                    <input type="checkbox" checked={!!selected[it.id]} onChange={() => toggle(it)} className="accent-[var(--qn-primary)]" />
                    <FileText className="w-4 h-4 text-[var(--qn-foreground-muted)]" /> <span className="text-sm text-[var(--qn-foreground)] flex-1 truncate">{it.name}</span>
                  </label>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 p-4 border-t border-[var(--qn-border)]">
          <div className="flex items-center gap-3">
            <span className="text-xs text-[var(--qn-foreground-muted)]">{selectedIds.length} selected</span>
            {stage === "files" && (
              <label className="flex items-center gap-1.5 text-xs text-[var(--qn-foreground-muted)] cursor-pointer" data-testid="connector-keep-synced">
                <input type="checkbox" checked={keepSynced} onChange={(e) => setKeepSynced(e.target.checked)} className="accent-[var(--qn-primary)]" />
                Keep this folder synced
              </label>
            )}
          </div>
          <button onClick={doImport} disabled={importing || selectedIds.length === 0} data-testid="connector-import-submit"
            className="flex items-center gap-2 text-sm rounded-lg bg-[var(--qn-primary)] text-white px-4 py-2 disabled:opacity-50">
            {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />} Import
          </button>
        </div>
      </motion.div>
    </div>
  );
}
